<script setup lang="ts">
	import { computed, reactive, ref } from 'vue'

	import useCompanyReportHotelsSummary from '@/modules/companies/composables/useCompanyReportHotelsSummary'

	interface Props {
		companyId?: number
		dateRange?: string[] | null
		creationDateRange?: string[] | null
		statusFilter?: string
		searchTravelerId?: number | null
		searchHotelId?: number | null
		createdAt?: string[] | null
		compact?: boolean
	}

	const props = defineProps<Props>()

	const toggleSpend = reactive({
		toggleTotalSpend: true,
		toggleAveragePerTrip: true,
		toggleTotalSavings: true,
	})

	// Hacer petición autónoma
	const { reportSummary, isLoading } = useCompanyReportHotelsSummary(
		computed(() => ({
			companyId: props.companyId,
			filters: {
				dates: props.dateRange,
				creationDates: props.creationDateRange,
				status: props.statusFilter !== 'all' ? props.statusFilter : undefined,
				travelerId: props.searchTravelerId || undefined,
				hotelId: props.searchHotelId || undefined,
				createdAt: props.createdAt || undefined,
			},
		}))
	)

	const formatCurrency = (value: number): string => {
		if (value >= 1000000) {
			return (
				new Intl.NumberFormat('es-MX', {
					style: 'currency',
					currency: 'MXN',
					minimumFractionDigits: 1,
					maximumFractionDigits: 1,
				}).format(value / 1000000) + ' M'
			)
		} else if (value >= 1000) {
			return (
				new Intl.NumberFormat('es-MX', {
					style: 'currency',
					currency: 'MXN',
					minimumFractionDigits: 1,
					maximumFractionDigits: 1,
				}).format(value / 1000) + ' K'
			)
		} else {
			return new Intl.NumberFormat('es-MX', {
				style: 'currency',
				currency: 'MXN',
				minimumFractionDigits: 0,
				maximumFractionDigits: 0,
			}).format(Math.round(value))
		}
	}

	const formatCurrencyFull = (value: number): string => {
		return new Intl.NumberFormat('es-MX', {
			style: 'currency',
			currency: 'MXN',
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(value)
	}

	// Computed para verificar si hay datos válidos (no todos en cero)
	const hasValidData = computed(() => {
		if (!reportSummary.value) return false

		const summary = reportSummary.value
		return (
			(summary.totalTrips && summary.totalTrips > 0) ||
			(summary.totalSpend && summary.totalSpend > 0) ||
			(summary.averagePerTrip && summary.averagePerTrip > 0) ||
			(summary.totalNights && summary.totalNights > 0) ||
			(summary.totalSavings && summary.totalSavings > 0) ||
			(summary.averageAnticipation && summary.averageAnticipation > 0)
		)
	})

	// Reportería de ahorros bloqueada por plan (free/start): el backend manda
	// savingsLocked (con el CTA de upgrade) y anula los montos. Cuando está
	// presente, mostramos "Cámbiate a PRO" en vez del $0 engañoso.
	const savingsLocked = computed(() => reportSummary.value?.savingsLocked)

	// Exponer isLoading para el componente padre
	defineExpose({
		isLoading,
	})
</script>

<template>
	<!-- Loading State -->
	<template v-if="isLoading">
		<!-- Compact loading -->
		<div v-if="compact" class="grid grid-cols-2 md:grid-cols-5 gap-3">
			<div
				v-for="i in 5"
				:key="i"
				class="bg-white rounded-xl border border-gray-100 shadow-sm p-4 animate-pulse"
			>
				<div class="flex items-center gap-2 mb-2">
					<div class="w-7 h-7 bg-gray-200 rounded-lg" />
					<div class="h-3 bg-gray-200 rounded w-16" />
				</div>
				<div class="h-6 bg-gray-200 rounded w-20" />
			</div>
		</div>
		<!-- Normal loading -->
		<div v-else class="space-y-3">
			<div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
				<div
					v-for="i in 4"
					:key="i"
					class="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden animate-pulse"
				>
					<div class="h-1 bg-gray-200" />
					<div class="p-4 space-y-2">
						<div class="flex justify-between items-center">
							<div class="h-2.5 bg-gray-200 rounded w-1/2" />
							<div class="w-7 h-7 rounded-lg bg-gray-200" />
						</div>
						<div class="h-6 bg-gray-200 rounded w-1/3 mt-1" />
						<div class="h-2.5 bg-gray-100 rounded w-2/3" />
					</div>
				</div>
			</div>
			<div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
				<div
					v-for="i in 3"
					:key="i"
					class="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden animate-pulse"
				>
					<div class="h-1 bg-gray-200" />
					<div class="p-4 space-y-2">
						<div class="flex justify-between items-center">
							<div class="h-2.5 bg-gray-200 rounded w-1/2" />
							<div class="w-7 h-7 rounded-lg bg-gray-200" />
						</div>
						<div class="h-6 bg-gray-200 rounded w-1/3 mt-1" />
						<div class="h-2.5 bg-gray-100 rounded w-2/3" />
					</div>
				</div>
			</div>
		</div>
	</template>

	<!-- KPIs Grid -->
	<div
		v-else-if="reportSummary && hasValidData"
		:class="compact ? '' : 'space-y-6'"
	>
		<!-- Versión Compacta -->
		<div
			v-if="compact"
			class="grid grid-cols-2 md:grid-cols-5 gap-3"
		>
			<!-- Total de Viajes -->
			<div class="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
				<div class="flex items-center gap-2 mb-2">
					<div class="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
						<span class="material-symbols-outlined text-blue-600 text-sm">luggage</span>
					</div>
					<p class="text-xs font-medium text-gray-500 truncate">Viajes</p>
				</div>
				<p class="text-xl font-bold text-gray-900 tabular-nums">{{ reportSummary?.totalTrips }}</p>
			</div>

			<!-- Gasto Total -->
			<div class="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
				<div class="flex items-center gap-2 mb-2">
					<div class="w-7 h-7 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
						<span class="material-symbols-outlined text-emerald-600 text-sm">payments</span>
					</div>
					<p class="text-xs font-medium text-gray-500 truncate">Gasto Total</p>
					<button
						@click="() => { toggleSpend.toggleTotalSpend = !toggleSpend.toggleTotalSpend }"
						class="ml-auto text-gray-400 hover:text-gray-600 transition-colors"
						v-tooltip="`${toggleSpend.toggleTotalSpend ? 'Mostrar formato completo' : 'Mostrar formato corto'}`"
					>
						<span class="material-symbols-outlined text-sm">visibility</span>
					</button>
				</div>
				<div class="flex items-baseline gap-1">
					<p class="text-xl font-bold text-gray-900 tabular-nums break-all">
						{{
							toggleSpend.toggleTotalSpend ?
								formatCurrency(reportSummary.totalSpend)
							:	formatCurrencyFull(reportSummary.totalSpend)
						}}
					</p>
					<span class="text-xs text-gray-400">MXN</span>
				</div>
			</div>

			<!-- Promedio por Viaje -->
			<div class="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
				<div class="flex items-center gap-2 mb-2">
					<div class="w-7 h-7 bg-violet-50 rounded-lg flex items-center justify-center flex-shrink-0">
						<span class="material-symbols-outlined text-violet-600 text-sm">trending_up</span>
					</div>
					<p class="text-xs font-medium text-gray-500 truncate">Promedio</p>
					<button
						@click="() => { toggleSpend.toggleAveragePerTrip = !toggleSpend.toggleAveragePerTrip }"
						class="ml-auto text-gray-400 hover:text-gray-600 transition-colors"
						v-tooltip="`${toggleSpend.toggleAveragePerTrip ? 'Mostrar formato completo' : 'Mostrar formato corto'}`"
					>
						<span class="material-symbols-outlined text-sm">visibility</span>
					</button>
				</div>
				<div class="flex items-baseline gap-1">
					<p class="text-xl font-bold text-gray-900 tabular-nums break-all">
						{{
							toggleSpend.toggleAveragePerTrip ?
								formatCurrency(reportSummary.averagePerTrip)
							:	formatCurrencyFull(reportSummary.averagePerTrip)
						}}
					</p>
					<span class="text-xs text-gray-400">MXN</span>
				</div>
			</div>

			<!-- Total de Noches -->
			<div class="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
				<div class="flex items-center gap-2 mb-2">
					<div class="w-7 h-7 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
						<span class="material-symbols-outlined text-amber-600 text-sm">hotel</span>
					</div>
					<p class="text-xs font-medium text-gray-500 truncate">Cuartos Noche</p>
				</div>
				<div class="flex items-baseline gap-2">
					<p class="text-xl font-bold text-gray-900 tabular-nums">{{ reportSummary?.totalNights }}</p>
					<span class="text-xs text-gray-400">
						/ {{ reportSummary?.totalTrips > 0 ? (reportSummary.totalNights / reportSummary.totalTrips).toFixed(1) : 0 }} prom.
					</span>
				</div>
			</div>

			<!-- Ahorros -->
			<div class="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
				<div class="flex items-center gap-2 mb-2">
					<div class="w-7 h-7 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
						<span class="material-symbols-outlined text-emerald-600 text-sm">savings</span>
					</div>
					<p class="text-xs font-medium text-gray-500 truncate">Ahorros</p>
					<button
						v-if="!savingsLocked"
						@click="() => { toggleSpend.toggleTotalSavings = !toggleSpend.toggleTotalSavings }"
						class="ml-auto text-gray-400 hover:text-gray-600 transition-colors"
						v-tooltip="`${toggleSpend.toggleTotalSavings ? 'Mostrar formato completo' : 'Mostrar formato corto'}`"
					>
						<span class="material-symbols-outlined text-sm">visibility</span>
					</button>
				</div>
				<!-- Plan sin ahorros (free/start): CTA de upgrade en vez del $0 -->
				<p
					v-if="savingsLocked"
					class="text-sm font-semibold text-emerald-700"
					v-tooltip="savingsLocked.message"
				>
					Cámbiate a PRO
				</p>
				<div v-else class="flex items-baseline gap-1">
					<p class="text-xl font-bold text-gray-900 tabular-nums break-all">
						{{
							toggleSpend.toggleTotalSavings ?
								formatCurrency(reportSummary.totalSavings)
							:	formatCurrencyFull(reportSummary.totalSavings)
						}}
					</p>
					<span class="text-xs text-gray-400">MXN</span>
				</div>
			</div>

			<!-- Leyenda de iconos móvil -->
			<div class="col-span-2 md:col-span-5 mt-1 flex flex-wrap items-center gap-3 text-xs text-gray-500">
				<span class="font-medium">Iconos:</span>
				<div class="flex items-center gap-1">
					<span class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-100 border border-amber-200">
						<span class="material-symbols-outlined text-amber-600 text-[10px]">schedule</span>
					</span>
					<span>Preliminar</span>
				</div>
				<!-- Sin savings_reports en el plan las filas no traen ahorro: leyenda fuera -->
				<div v-if="!savingsLocked" class="flex items-center gap-1">
					<span class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-100 border border-emerald-200">
						<span class="material-symbols-outlined text-emerald-600 text-[10px]">savings</span>
					</span>
					<span>Ahorro</span>
				</div>
			</div>
		</div>

		<!-- Versión Normal - Completa -->
		<template v-else>
			<!-- Primera fila: Métricas Principales -->
			<div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
				<!-- Total de Viajes -->
				<div class="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
					<div class="h-1 bg-blue-600" />
					<div class="p-4">
						<div class="flex items-start justify-between mb-2">
							<p class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Total de Viajes</p>
							<div class="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
								<span class="material-symbols-outlined text-blue-600 text-sm">luggage</span>
							</div>
						</div>
						<p class="text-2xl font-bold text-gray-900 tabular-nums">{{ reportSummary?.totalTrips }}</p>
						<p class="text-xs text-gray-400 mt-0.5">
							<span class="font-medium text-emerald-600">{{ reportSummary?.confirmedTrips ?? 0 }}</span> conf.
							<span class="mx-1 text-gray-300">·</span>
							<span class="font-medium text-red-500">{{ reportSummary?.cancelledTrips ?? 0 }}</span> canc.
						</p>
					</div>
				</div>

				<!-- Gasto Total -->
				<div class="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
					<div class="h-1 bg-emerald-500" />
					<div class="p-4">
						<div class="flex items-start justify-between mb-2">
							<p class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Gasto Total</p>
							<div class="flex items-center gap-1 flex-shrink-0">
								<button
									@click="() => { toggleSpend.toggleTotalSpend = !toggleSpend.toggleTotalSpend }"
									class="text-gray-400 hover:text-gray-600 transition-colors p-0.5 rounded hover:bg-gray-50"
									v-tooltip="`${toggleSpend.toggleTotalSpend ? 'Mostrar formato completo' : 'Mostrar formato corto'}`"
								>
									<span class="material-symbols-outlined text-sm">visibility</span>
								</button>
								<div class="w-7 h-7 bg-emerald-50 rounded-lg flex items-center justify-center">
									<span class="material-symbols-outlined text-emerald-600 text-sm">payments</span>
								</div>
							</div>
						</div>
						<div class="flex items-baseline gap-1.5">
							<p class="text-2xl font-bold text-gray-900 tabular-nums break-all">
								{{
									toggleSpend.toggleTotalSpend ?
										formatCurrency(reportSummary.totalSpend)
									:	formatCurrencyFull(reportSummary.totalSpend)
								}}
							</p>
							<span class="text-xs text-gray-400">MXN</span>
						</div>
						<p class="text-xs text-gray-400 mt-0.5">gasto total del período</p>
					</div>
				</div>

				<!-- Promedio por Viaje -->
				<div class="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
					<div class="h-1 bg-violet-500" />
					<div class="p-4">
						<div class="flex items-start justify-between mb-2">
							<p class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Promedio por Viaje</p>
							<div class="flex items-center gap-1 flex-shrink-0">
								<button
									@click="() => { toggleSpend.toggleAveragePerTrip = !toggleSpend.toggleAveragePerTrip }"
									class="text-gray-400 hover:text-gray-600 transition-colors p-0.5 rounded hover:bg-gray-50"
									v-tooltip="`${toggleSpend.toggleAveragePerTrip ? 'Mostrar formato completo' : 'Mostrar formato corto'}`"
								>
									<span class="material-symbols-outlined text-sm">visibility</span>
								</button>
								<div class="w-7 h-7 bg-violet-50 rounded-lg flex items-center justify-center">
									<span class="material-symbols-outlined text-violet-600 text-sm">trending_up</span>
								</div>
							</div>
						</div>
						<div class="flex items-baseline gap-1.5">
							<p class="text-2xl font-bold text-gray-900 tabular-nums break-all">
								{{
									toggleSpend.toggleAveragePerTrip ?
										formatCurrency(reportSummary.averagePerTrip)
									:	formatCurrencyFull(reportSummary.averagePerTrip)
								}}
							</p>
							<span class="text-xs text-gray-400">MXN</span>
						</div>
						<p class="text-xs text-gray-400 mt-0.5">por reserva en promedio</p>
					</div>
				</div>

				<!-- Cuartos Noche -->
				<div class="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
					<div class="h-1 bg-amber-500" />
					<div class="p-4">
						<div class="flex items-start justify-between mb-2">
							<p class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Cuartos Noche</p>
							<div class="w-7 h-7 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
								<span class="material-symbols-outlined text-amber-600 text-sm">hotel</span>
							</div>
						</div>
						<p class="text-2xl font-bold text-gray-900 tabular-nums">{{ reportSummary?.totalNights }}</p>
						<p class="text-xs text-gray-400 mt-0.5">
							noches
							<span class="mx-1 text-gray-300">·</span>
							<span class="font-medium text-amber-600">{{ reportSummary?.totalTrips > 0 ? (reportSummary.totalNights / reportSummary.totalTrips).toFixed(1) : 0 }}</span> prom.
						</p>
					</div>
				</div>
			</div>

			<!-- Segunda fila: Métricas Adicionales -->
			<div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
				<!-- Ahorros -->
				<div class="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
					<div class="h-1 bg-[#021F58]" />
					<div class="p-4">
						<div class="flex items-start justify-between mb-2">
							<p class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Ahorros Totales</p>
							<div class="flex items-center gap-1 flex-shrink-0">
								<button
									v-if="!savingsLocked"
									@click="() => { toggleSpend.toggleTotalSavings = !toggleSpend.toggleTotalSavings }"
									class="text-gray-400 hover:text-gray-600 transition-colors p-0.5 rounded hover:bg-gray-50"
									v-tooltip="`${toggleSpend.toggleTotalSavings ? 'Mostrar formato completo' : 'Mostrar formato corto'}`"
								>
									<span class="material-symbols-outlined text-sm">visibility</span>
								</button>
								<div class="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center">
									<span class="material-symbols-outlined text-[#021F58] text-sm">savings</span>
								</div>
							</div>
						</div>
						<!-- Plan sin ahorros (free/start): CTA de upgrade en vez del $0/% -->
						<p
							v-if="savingsLocked"
							class="text-base font-bold text-[#021F58] leading-snug"
						>
							{{ savingsLocked.message }}
						</p>
						<template v-else>
							<div class="flex items-baseline gap-1.5">
								<p class="text-2xl font-bold text-gray-900 tabular-nums break-all">
									{{
										toggleSpend.toggleTotalSavings ?
											formatCurrency(reportSummary.totalSavings)
										:	formatCurrencyFull(reportSummary.totalSavings)
									}}
								</p>
								<span class="text-xs text-gray-400">MXN</span>
							</div>
							<p class="text-xs text-gray-400 mt-0.5">{{ reportSummary.savingsPercentage }}% del gasto total</p>
						</template>
					</div>
				</div>

				<!-- Anticipación promedio -->
				<div class="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
					<div class="h-1 bg-sky-500" />
					<div class="p-4">
						<div class="flex items-start justify-between mb-2">
							<p class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Anticipación de Compra</p>
							<div class="w-7 h-7 bg-sky-50 rounded-lg flex items-center justify-center flex-shrink-0">
								<span class="material-symbols-outlined text-sky-600 text-sm">schedule</span>
							</div>
						</div>
						<p class="text-2xl font-bold text-gray-900 tabular-nums">{{ reportSummary?.averageAnticipation }}</p>
						<p class="text-xs text-gray-400 mt-0.5">días antes del viaje en promedio</p>
					</div>
				</div>

				<!-- Tiempo de Aprobación -->
				<div class="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
					<div class="h-1 bg-purple-500" />
					<div class="p-4">
						<div class="flex items-start justify-between mb-2">
							<p class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Tiempo de Aprobación</p>
							<div class="w-7 h-7 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0">
								<span class="material-symbols-outlined text-purple-600 text-sm">timer</span>
							</div>
						</div>
						<div class="flex items-baseline gap-1.5">
							<p class="text-2xl font-bold text-gray-900 tabular-nums">
								{{ reportSummary?.averageApprovalTime?.toFixed(1) || 0 }}
							</p>
							<span class="text-xs text-gray-400">hrs</span>
						</div>
						<p class="text-xs text-gray-400 mt-0.5">promedio por solicitud</p>
					</div>
				</div>
			</div>

			<!-- Leyenda de iconos -->
			<div class="flex flex-wrap items-center gap-4 text-xs text-gray-500">
				<span class="font-medium">Iconos en tabla:</span>
				<div class="flex items-center gap-1">
					<span class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 border border-amber-200">
						<span class="material-symbols-outlined text-amber-600 text-xs">schedule</span>
					</span>
					<span>Reserva preliminar</span>
				</div>
				<!-- Sin savings_reports en el plan las filas no traen ahorro: leyenda fuera -->
				<div v-if="!savingsLocked" class="flex items-center gap-1">
					<span class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 border border-emerald-200">
						<span class="material-symbols-outlined text-emerald-600 text-xs">savings</span>
					</span>
					<span>Ahorro obtenido</span>
				</div>
			</div>
		</template>
	</div>

	<!-- No Data State -->
	<div
		v-else
		class="flex items-center justify-center py-16"
	>
		<div class="text-center">
			<div class="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
				<span class="material-symbols-outlined text-4xl text-gray-300">summarize</span>
			</div>
			<p class="text-base font-semibold text-gray-700">Sin datos disponibles</p>
			<p class="mt-1 text-sm text-gray-400">
				No se encontró información de resumen en el período seleccionado
			</p>
		</div>
	</div>
</template>
